/**
 * 代家长报名对话框（ADM-04，enrollment-application:SC-36）。
 *
 * 两步向导：学生匹配（姓名/手机号搜索 + 常驻内联新建）→ 课程与报名信息
 * （校区 + 单选在售 SKU Select + Descriptions + 来源/分数/备注）。
 * 一次报名一个 SKU，不采集来源商品（sourceProductId 恒 null）。
 */
import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Descriptions,
  Form,
  Input,
  InputNumber,
  Radio,
  Select,
  Space,
  Steps,
  message,
} from 'antd';
import { DraggableModal } from '../../components/DraggableModal';
import { courseSkuApi, type CourseSkuItem } from '../../api/course-skus.api';
import { orgCampusApi, type CampusItem } from '../../api/campuses.api';
import { studentsApi, type StudentSearchItem } from '../../api/students.api';
import { productApi, type ProductItem } from '../../api/products.api';
import { enrollmentsApi } from '../../api/enrollments.api';
import { subjectsApi, type SubjectItem } from '../../api/subjects.api';
import { systemSettingsApi, type EnrollmentSourceItem } from '../../api/system-settings.api';
import { gradeStageLabel, toSelectOptions } from '../../api/labels';
import { startAction } from '../../observability/otel';

export interface EnrollmentCreateModalProps {
  open: boolean;
  onClose: () => void;
  onSubmitted: () => void;
}

const FALLBACK_SOURCES: EnrollmentSourceItem[] = [
  { value: 'STAFF_ASSISTED', label: '员工代建' },
  { value: 'PARENT_SELF', label: '家长自助' },
  { value: 'SHARE_REFERRAL', label: '好友分享' },
];

const RELATIONSHIP_OPTIONS = [
  { value: 'FATHER', label: '父亲' },
  { value: 'MOTHER', label: '母亲' },
  { value: 'GRANDPA', label: '爷爷' },
  { value: 'GRANDMA', label: '奶奶' },
  { value: 'OTHER', label: '其他' },
];

/** 分→元展示（与原型 formatAmount 一致，无千分位、无空格）。 */
function formatYuan(fen: number): string {
  const sign = fen < 0 ? '-' : '';
  const abs = Math.abs(fen);
  return `¥${sign}${Math.floor(abs / 100)}.${(abs % 100).toString().padStart(2, '0')}`;
}

/** 在售商品中包含该 SKU 的最低定价（无在售打包价则不可报名）。 */
function skuStartingPrice(skuId: string, products: ProductItem[]): number | undefined {
  const prices = products
    .filter((p) => p.status === 'ON_SHELF' && p.skuConfigs.some((c) => c.skuId === skuId))
    .map((p) => p.price);
  if (prices.length === 0) return undefined;
  return Math.min(...prices);
}

export function EnrollmentCreateModal({ open, onClose, onSubmitted }: EnrollmentCreateModalProps) {
  const [form] = Form.useForm();
  const [step, setStep] = useState(0);
  const [campuses, setCampuses] = useState<CampusItem[]>([]);
  const [skus, setSkus] = useState<CourseSkuItem[]>([]);
  const [products, setProducts] = useState<ProductItem[]>([]);
  const [subjects, setSubjects] = useState<SubjectItem[]>([]);
  const [sources, setSources] = useState<EnrollmentSourceItem[]>(FALLBACK_SOURCES);
  const [skuId, setSkuId] = useState<string | undefined>();
  const [matchedStudentId, setMatchedStudentId] = useState<string | null>(null);
  const [matchedStudents, setMatchedStudents] = useState<StudentSearchItem[]>([]);
  const [keyword, setKeyword] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchDone, setSearchDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setStep(0);
    setSkuId(undefined);
    setMatchedStudentId(null);
    setMatchedStudents([]);
    setKeyword('');
    setSearchDone(false);
    setError(null);
    form.resetFields();
    orgCampusApi.listCampuses({ page: 1, pageSize: 100 }).then((r) => setCampuses(r.items));
    courseSkuApi.listCourseSkus({ page: 1, pageSize: 100 }).then((r) => {
      setSkus(r.items.filter((s) => s.status === 'ON_SHELF'));
    });
    productApi
      .listProducts({ page: 1, pageSize: 100, status: 'ON_SHELF' })
      .then((r) => setProducts(r.items))
      .catch(() => setProducts([]));
    subjectsApi
      .listSubjects({ page: 1, pageSize: 100 })
      .then((r) => setSubjects(r.items))
      .catch(() => setSubjects([]));
    systemSettingsApi
      .getSettings()
      .then((s) => {
        if (s.enrollmentSources?.length) setSources(s.enrollmentSources);
      })
      .catch(() => setSources(FALLBACK_SOURCES));
  }, [open, form]);

  const selectableSkus = useMemo(
    () =>
      skus.filter((s) => skuStartingPrice(s.id, products) !== undefined),
    [skus, products],
  );

  const selectedSku = useMemo(
    () => selectableSkus.find((s) => s.id === skuId),
    [selectableSkus, skuId],
  );

  /** 所选 SKU 涉及科目（一次报名一个 SKU，即单科目逐科行）。 */
  const selectedSubject = useMemo(
    () => (selectedSku ? subjects.find((x) => x.id === selectedSku.subjectId) : undefined),
    [selectedSku, subjects],
  );

  /** 分数录入上限 = 所选 SKU 科目满分（未选 SKU 时默认 100）。 */
  const scoreMax = selectedSubject?.fullScore ?? 100;

  const searchStudent = async (value?: string) => {
    const kw = (value ?? keyword).trim();
    if (!kw) {
      setMatchedStudents([]);
      setMatchedStudentId(null);
      setSearchDone(false);
      return;
    }
    setSearching(true);
    try {
      const hits = await studentsApi.searchStudents(kw);
      setMatchedStudents(hits);
      setMatchedStudentId(hits.length === 1 ? hits[0]!.id : null);
      setSearchDone(true);
    } catch {
      setMatchedStudents([]);
      setMatchedStudentId(null);
      setSearchDone(false);
      setError('学生搜索失败，请稍后重试');
    } finally {
      setSearching(false);
    }
  };

  const watchedNewName = Form.useWatch('newName', form) as string | undefined;
  const canNext = !!matchedStudentId || !!watchedNewName?.trim();

  const goNext = async () => {
    if (matchedStudentId) {
      setError(null);
      setStep(1);
      return;
    }
    const name = (form.getFieldValue('newName') as string | undefined)?.trim();
    if (!name) {
      setError('请匹配已有学生或填写新学生姓名');
      return;
    }
    setError(null);
    setStep(1);
  };

  const submit = async () => {
    const values = await form.validateFields().catch(() => null);
    if (!values) return;
    if (!skuId) {
      setError('请选择课程SKU');
      return;
    }
    setSubmitting(true);
    setError(null);
    const span = startAction('action: enrollment.submit');
    try {
      const payload = {
        ...(matchedStudentId
          ? { studentId: matchedStudentId }
          : {
              newStudent: {
                name: values.newName,
                gender: values.newGender,
                gradeStage: values.newGradeStage,
                homeCampusId: values.campusId,
                guardianMobile: values.guardianMobile,
                guardians: [
                  {
                    name: values.guardianName ?? '家长',
                    relationship: values.newRelationship ?? 'FATHER',
                  },
                ],
              },
            }),
        skuIds: [skuId],
        campusId: values.campusId,
        sourceProductId: null,
        source: values.source,
        scores:
          values.score !== null && values.score !== undefined && selectedSku
            ? { [selectedSku.subjectId]: values.score }
            : null,
        note: values.note ?? null,
        idempotencyKey: `enr-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      };
      const result = await enrollmentsApi.submitEnrollment(payload);
      message.success(
        `报名成功（报名单 #${result.submissionId.slice(0, 8)}），已赠试课 ${result.applications
          .map((i) => i.trialGranted)
          .join('/')} 次`,
      );
      span?.end();
      onSubmitted();
      onClose();
      form.resetFields();
    } catch (e) {
      setError(e instanceof Error ? e.message : '提交失败');
      span?.end();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DraggableModal
      title="代家长报名"
      open={open}
      onCancel={onClose}
      footer={null}
      width={720}
      destroyOnHidden
    >
      <Steps
        current={step}
        items={[{ title: '学生匹配' }, { title: '课程与报名信息' }]}
        style={{ marginBottom: 20 }}
      />
      {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 12 }} />}
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          source: 'STAFF_ASSISTED',
          newGender: 'MALE',
          newGradeStage: 'PRIMARY_LOW',
          newRelationship: 'FATHER',
        }}
      >
        {step === 0 && (
          <div>
            <Input.Search
              placeholder="按姓名或监护人手机号搜索已有学生"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              onSearch={searchStudent}
              loading={searching}
              allowClear
              enterButton="搜索"
              style={{ marginBottom: 12 }}
              data-testid="enrollment-search-mobile"
            />
            {searchDone && matchedStudents.length === 0 && (
              <Alert
                type="info"
                showIcon
                message="未找到匹配学生"
                description="可核对关键词后重试，或在下方直接新建学生"
                style={{ marginBottom: 12 }}
              />
            )}
            {matchedStudents.length > 0 && (
              <div style={{ marginBottom: 12 }} data-testid="enrollment-student-results">
                {matchedStudents.map((s) => (
                  <div
                    key={s.id}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '8px 12px',
                      borderBottom: '1px solid #f0f0f0',
                    }}
                    data-testid={`enrollment-student-card-${s.id}`}
                  >
                    <span>
                      {s.name}（{s.guardianMobile}）
                    </span>
                    <Button
                      size="small"
                      type={matchedStudentId === s.id ? 'primary' : 'default'}
                      onClick={() => setMatchedStudentId(s.id)}
                    >
                      {matchedStudentId === s.id ? '已关联' : '关联此学生'}
                    </Button>
                  </div>
                ))}
              </div>
            )}
            <div style={{ background: '#fafafa', padding: 16, borderRadius: 8 }}>
              <p style={{ marginTop: 0, marginBottom: 12, fontWeight: 600 }}>或新建学生</p>
              <Space wrap>
                <Form.Item label="姓名" name="newName" style={{ marginBottom: 8 }}>
                  <Input
                    style={{ width: 140 }}
                    maxLength={50}
                    disabled={!!matchedStudentId}
                    data-testid="enrollment-new-name"
                  />
                </Form.Item>
                <Form.Item label="性别" name="newGender" style={{ marginBottom: 8 }}>
                  <Radio.Group disabled={!!matchedStudentId}>
                    <Radio value="MALE">男</Radio>
                    <Radio value="FEMALE">女</Radio>
                  </Radio.Group>
                </Form.Item>
                <Form.Item label="年级/学段" name="newGradeStage" style={{ marginBottom: 8 }}>
                  <Select
                    style={{ width: 130 }}
                    disabled={!!matchedStudentId}
                    options={toSelectOptions(gradeStageLabel)}
                  />
                </Form.Item>
                <Form.Item label="监护人称呼" name="guardianName" style={{ marginBottom: 8 }}>
                  <Input style={{ width: 120 }} maxLength={50} disabled={!!matchedStudentId} />
                </Form.Item>
                <Form.Item label="与孩子关系" name="newRelationship" style={{ marginBottom: 8 }}>
                  <Select
                    style={{ width: 110 }}
                    disabled={!!matchedStudentId}
                    options={RELATIONSHIP_OPTIONS}
                  />
                </Form.Item>
                <Form.Item label="监护人手机号" name="guardianMobile" style={{ marginBottom: 8 }}>
                  <Input style={{ width: 150 }} maxLength={11} disabled={!!matchedStudentId} />
                </Form.Item>
              </Space>
            </div>
            <div style={{ marginTop: 16, textAlign: 'right' }}>
              <Button type="primary" onClick={() => void goNext()} disabled={!canNext}>
                下一步
              </Button>
            </div>
          </div>
        )}
        {step === 1 && (
          <div>
            <Space wrap size={16} style={{ marginBottom: 12 }}>
              <Form.Item label="校区" name="campusId" rules={[{ required: true, message: '请选择校区' }]} style={{ marginBottom: 8 }}>
                <Select
                  style={{ width: 150 }}
                  options={campuses.map((c) => ({ value: c.id, label: c.name }))}
                />
              </Form.Item>
              <div>
                <div style={{ fontSize: 12, color: '#888', marginBottom: 8 }}>课程SKU</div>
                <Select
                  style={{ width: 280 }}
                  placeholder="选择课程SKU"
                  showSearch
                  optionFilterProp="label"
                  value={skuId}
                  onChange={(v) => {
                    setSkuId(v);
                    // 切换 SKU 即切换科目，清空已填分数（录入上限随科目满分变化）
                    form.setFieldValue('score', null);
                  }}
                  data-testid="enrollment-sku-select"
                  options={selectableSkus.map((s) => ({
                    value: s.id,
                    label: `${s.name}（${formatYuan(skuStartingPrice(s.id, products) ?? s.listPrice)} 起）`,
                  }))}
                />
              </div>
            </Space>
            {selectedSku && (
              <Descriptions size="small" bordered column={1} style={{ marginBottom: 16 }}>
                <Descriptions.Item label="课时数">{selectedSku.entitlementQuantity} 课时</Descriptions.Item>
                <Descriptions.Item label="标价">{formatYuan(selectedSku.listPrice)}</Descriptions.Item>
                <Descriptions.Item label="试课赠送">{selectedSku.trialSessionCount} 次</Descriptions.Item>
              </Descriptions>
            )}
            <Form.Item label="报名来源" name="source" rules={[{ required: true }]}>
              <Select options={sources} />
            </Form.Item>
            {selectedSku && (
              <Form.Item
                label={`科目分数 · ${selectedSubject?.name ?? '该科目'}（选填，用于筛选分班，满分 ${scoreMax}）`}
                name="score"
                rules={[
                  {
                    type: 'integer',
                    min: 0,
                    max: scoreMax,
                    message: `分数必须为 0-${scoreMax} 的整数`,
                  },
                ]}
              >
                <InputNumber style={{ width: 120 }} placeholder={`0-${scoreMax}`} />
              </Form.Item>
            )}
            <Form.Item label="备注" name="note">
              <Input.TextArea maxLength={500} showCount rows={2} />
            </Form.Item>
            <div style={{ textAlign: 'right' }}>
              <Space>
                <Button onClick={() => setStep(0)}>返回</Button>
                <Button type="primary" loading={submitting} onClick={() => void submit()}>
                  提交
                </Button>
              </Space>
            </div>
          </div>
        )}
      </Form>
    </DraggableModal>
  );
}
